// Terminal Theme — Shared design constants for the developer-oriented aesthetic
// Import: import { tw, categoryColors, statusColors, groupColors, getCategoryStyle } from '../utils/terminalTheme';

// === TAILWIND CLASS CONSTANTS ===
export const tw = {
  // Stat cards
  statCard: 'bg-gray-800/80 border border-gray-700/50 rounded-lg p-5',
  statLabel: 'text-gray-500 text-xs font-mono uppercase tracking-wider mb-2',
  statValue: 'text-2xl font-bold font-mono',
  statValueCyan: 'text-2xl font-bold font-mono text-cyan-400',

  // Tables
  tableWrap: 'bg-gray-800/80 border border-gray-700/50 rounded-lg overflow-hidden',
  tableHeader: 'px-5 py-3 text-left text-xs font-mono text-gray-500 uppercase tracking-wider',
  tableHeaderRight: 'px-5 py-3 text-right text-xs font-mono text-gray-500 uppercase tracking-wider',
  tableRow: 'hover:bg-gray-700/20 transition-colors group',
  tableDivider: 'divide-y divide-gray-700/30',
  tableFooter: 'border-t border-gray-700/50 px-5 py-3 flex justify-between items-center bg-gray-900/30',
  tableCell: 'px-5 py-3.5',
  tableCellRight: 'px-5 py-3.5 text-right',

  // Forms
  input: 'w-full bg-gray-900 text-white rounded px-3 py-2 border border-gray-700 focus:border-cyan-500 focus:outline-none font-mono text-sm',
  select: 'w-full bg-gray-900 text-white rounded px-3 py-2 border border-gray-700 focus:border-cyan-500 focus:outline-none font-mono text-sm',
  label: 'block text-xs text-gray-500 font-mono mb-1',
  formWrap: 'bg-gray-800/80 border border-cyan-500/20 rounded-lg p-6',
  formTitle: 'text-lg font-mono text-cyan-400 mb-4',

  // Buttons
  btnPrimary: 'bg-cyan-600 hover:bg-cyan-700 text-white px-4 py-2 rounded-md font-mono text-sm transition-colors',
  btnSecondary: 'bg-gray-700 hover:bg-gray-600 text-gray-300 px-4 py-2 rounded-md font-mono text-sm transition-colors',
  btnDanger: 'text-gray-500 hover:text-red-400 text-xs font-mono transition-colors',
  btnGhost: 'text-gray-500 hover:text-cyan-400 text-xs font-mono transition-colors',

  // Loading
  loading: 'animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500',
  loadingContainer: 'flex items-center justify-center h-screen',

  // Badges
  badge: (colors: { bg: string; text: string; border: string }) =>
    `inline-flex px-2 py-0.5 rounded text-xs font-mono ${colors.bg} ${colors.text} border ${colors.border}`,

  // Actions
  hoverReveal: 'opacity-0 group-hover:opacity-100 transition-opacity',

  // Layout
  page: 'p-8 max-w-7xl mx-auto',
  pageWide: 'p-8',
  sectionTitle: 'text-gray-400 text-sm font-mono',

  // Filter badges
  filterActive: 'px-3 py-1.5 rounded-md text-xs font-mono transition-all bg-cyan-600 text-white',
  filterInactive: 'px-3 py-1.5 rounded-md text-xs font-mono transition-all bg-gray-800 text-gray-400 hover:text-white border border-gray-700',

  // Empty state
  empty: 'text-center py-12 text-gray-500 font-mono text-sm',

  // Text
  link: 'text-white hover:text-cyan-400 transition-colors',
  muted: 'text-gray-500 text-xs font-mono',
  cyan: 'text-cyan-400',
  green: 'text-green-500',
} as const;

// === JetBrains Mono style for headers ===
export const monoFont = { fontFamily: "'JetBrains Mono', monospace" };

// === CATEGORY COLOR MAPS ===

// Tech Stack categories
export const techCategoryColors: Record<string, { bg: string; text: string; border: string }> = {
  'AI/LLM':         { bg: 'bg-purple-500/15', text: 'text-purple-400', border: 'border-purple-500/30' },
  'Development':    { bg: 'bg-cyan-500/15',   text: 'text-cyan-400',   border: 'border-cyan-500/30' },
  'Data/Scraping':  { bg: 'bg-amber-500/15',  text: 'text-amber-400',  border: 'border-amber-500/30' },
  'Design':         { bg: 'bg-pink-500/15',   text: 'text-pink-400',   border: 'border-pink-500/30' },
  'Cloud Storage':  { bg: 'bg-blue-500/15',   text: 'text-blue-400',   border: 'border-blue-500/30' },
  'Video/Media':    { bg: 'bg-red-500/15',    text: 'text-red-400',    border: 'border-red-500/30' },
  'Business Tools': { bg: 'bg-emerald-500/15', text: 'text-emerald-400', border: 'border-emerald-500/30' },
  'Hosting':        { bg: 'bg-orange-500/15', text: 'text-orange-400', border: 'border-orange-500/30' },
  'SaaS':           { bg: 'bg-indigo-500/15', text: 'text-indigo-400', border: 'border-indigo-500/30' },
  'Other':          { bg: 'bg-gray-500/15',   text: 'text-gray-400',   border: 'border-gray-500/30' },
};

// Property status colors
export const statusColors: Record<string, { bg: string; text: string; border: string }> = {
  'occupied':    { bg: 'bg-emerald-500/15', text: 'text-emerald-400', border: 'border-emerald-500/30' },
  'vacant':      { bg: 'bg-red-500/15',     text: 'text-red-400',     border: 'border-red-500/30' },
  'for-sale':    { bg: 'bg-blue-500/15',    text: 'text-blue-400',    border: 'border-blue-500/30' },
  'renovation':  { bg: 'bg-amber-500/15',   text: 'text-amber-400',   border: 'border-amber-500/30' },
  'pending':     { bg: 'bg-yellow-500/15',  text: 'text-yellow-400',  border: 'border-yellow-500/30' },
};

// PocketSmith account group colors
export const groupColors: Record<string, { bg: string; text: string; border: string }> = {
  'PERSONAL':   { bg: 'bg-amber-500/15',   text: 'text-amber-400',   border: 'border-amber-500/30' },
  'BUSINESS':   { bg: 'bg-blue-500/15',    text: 'text-blue-400',    border: 'border-blue-500/30' },
  'INVESTMENT': { bg: 'bg-emerald-500/15',  text: 'text-emerald-400', border: 'border-emerald-500/30' },
};

// Document/expense categories
export const expenseCategoryColors: Record<string, { bg: string; text: string; border: string }> = {
  'Mortgage':       { bg: 'bg-blue-500/15',    text: 'text-blue-400',    border: 'border-blue-500/30' },
  'HOA':            { bg: 'bg-purple-500/15',  text: 'text-purple-400',  border: 'border-purple-500/30' },
  'Utilities':      { bg: 'bg-amber-500/15',   text: 'text-amber-400',   border: 'border-amber-500/30' },
  'Maintenance':    { bg: 'bg-orange-500/15',  text: 'text-orange-400',  border: 'border-orange-500/30' },
  'Insurance':      { bg: 'bg-cyan-500/15',    text: 'text-cyan-400',    border: 'border-cyan-500/30' },
  'Tax':            { bg: 'bg-red-500/15',     text: 'text-red-400',     border: 'border-red-500/30' },
  'Rent':           { bg: 'bg-emerald-500/15', text: 'text-emerald-400', border: 'border-emerald-500/30' },
  'Vehicle Payment':{ bg: 'bg-indigo-500/15',  text: 'text-indigo-400',  border: 'border-indigo-500/30' },
  'Uncategorized':  { bg: 'bg-gray-500/15',    text: 'text-gray-400',    border: 'border-gray-500/30' },
};

// HOA status colors
export const hoaStatusColors: Record<string, { bg: string; text: string; border: string }> = {
  'current':  { bg: 'bg-emerald-500/15', text: 'text-emerald-400', border: 'border-emerald-500/30' },
  'overdue':  { bg: 'bg-red-500/15',     text: 'text-red-400',     border: 'border-red-500/30' },
  'upcoming': { bg: 'bg-amber-500/15',   text: 'text-amber-400',   border: 'border-amber-500/30' },
  'paid':     { bg: 'bg-cyan-500/15',    text: 'text-cyan-400',    border: 'border-cyan-500/30' },
};

// Generic helper — get style or fallback to gray
export const getCategoryStyle = (
  category: string,
  colorMap: Record<string, { bg: string; text: string; border: string }>
) => {
  return colorMap[category] || { bg: 'bg-gray-500/15', text: 'text-gray-400', border: 'border-gray-500/30' };
};

// Chart.js hex colors for categories
export const chartCategoryColors: Record<string, string> = {
  Mortgage: '#3b82f6', HOA: '#a855f7', Utilities: '#f59e0b',
  Maintenance: '#f97316', Insurance: '#06b6d4', Tax: '#ef4444',
  Rent: '#10b981', Income: '#22c55e', Transfer: '#6b7280',
  'Property Tax': '#dc2626', 'Vehicle Payment': '#6366f1',
};
export const getChartColor = (cat: string): string => chartCategoryColors[cat] || '#6b7280';
